'use client'

import Link from 'next/link'
import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { createClient } from '@/lib/supabase/client'

type Step = 'orgType' | 'orgDetails' | 'accountDetails' | 'terms' | 'confirmation'

interface OrgTypeOption {
  id: string
  title: string
  description: string
  enabled: boolean
}

const ORG_TYPES: OrgTypeOption[] = [
  { id: 'developer', title: 'Project Developer', description: 'Submit marine conservation projects for credit issuance', enabled: true },
  { id: 'vvb', title: 'VVB (Validation & Verification Body)', description: 'Provide independent validation and verification services', enabled: true },
  { id: 'corporate', title: 'Corporate', description: 'Purchase and retire marine conservation credits', enabled: true },
  { id: 'governance', title: 'Governance', description: 'Contact us for Governance, Academic, or NGO accounts', enabled: false },
  { id: 'academic', title: 'Academic', description: 'Contact us for Governance, Academic, or NGO accounts', enabled: false },
  { id: 'ngo', title: 'NGO', description: 'Contact us for Governance, Academic, or NGO accounts', enabled: false },
]

const COUNTRIES = ['United Kingdom', 'United States', 'Canada', 'Australia', 'Norway', 'Sweden', 'Germany', 'France', 'Japan', 'Singapore', 'Other']

const PASSWORD_REQUIREMENTS = [
  { regex: /.{12,}/, label: '12+ characters' },
  { regex: /[A-Z]/, label: 'Uppercase letter' },
  { regex: /[a-z]/, label: 'Lowercase letter' },
  { regex: /\d/, label: 'Number' },
  { regex: /[!@#$%^&*]/, label: 'Special character (!@#$%^&*)' },
]

function getPasswordStrength(password: string): { score: number; label: string; color: string } {
  let score = 0
  PASSWORD_REQUIREMENTS.forEach((req) => { if (req.regex.test(password)) score++ })
  if (score === 0) return { score: 0, label: 'Weak', color: 'var(--mcc-danger-fg)' }
  if (score <= 2) return { score: 1, label: 'Fair', color: '#f97316' }
  if (score <= 3) return { score: 2, label: 'Strong', color: 'var(--mcc-teal)' }
  return { score: 3, label: 'Very Strong', color: 'var(--mcc-sea)' }
}

export default function RegisterForm() {
  const router = useRouter()
  const [step, setStep] = useState<Step>('orgType')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [selectedOrgType, setSelectedOrgType] = useState<string | null>(null)
  const [orgName, setOrgName] = useState('')
  const [regNumber, setRegNumber] = useState('')
  const [country, setCountry] = useState('')
  const [website, setWebsite] = useState('')
  const [orgEmail, setOrgEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [agreeTerms, setAgreeTerms] = useState(false)
  const [agreePrivacy, setAgreePrivacy] = useState(false)
  const [agreeDataProcessing, setAgreeDataProcessing] = useState(false)
  const [agreeVVBTerms, setAgreeVVBTerms] = useState(false)

  const passwordStrength = getPasswordStrength(password)
  const isPasswordValid = PASSWORD_REQUIREMENTS.every((req) => req.regex.test(password))
  const passwordsMatch = password === confirmPassword && password.length > 0

  const progressPercentage: Record<Step, number> = { orgType: 20, orgDetails: 40, accountDetails: 60, terms: 80, confirmation: 100 }
  const stepLabels: Record<Step, string> = { orgType: 'Step 1 of 4: Organisation Type', orgDetails: 'Step 2 of 4: Organisation Details', accountDetails: 'Step 3 of 4: Account Details', terms: 'Step 4 of 4: Terms & Agreements', confirmation: '' }

  const handleNextFromOrgType = () => { if (!selectedOrgType) { setError('Please select an organization type'); return; } setError(null); setStep('orgDetails'); }
  const handleNextFromOrgDetails = () => { if (!orgName || !regNumber || !country || !orgEmail) { setError('Please fill in all required fields'); return; } setError(null); setStep('accountDetails'); }
  const handleNextFromAccountDetails = () => { if (!firstName || !lastName || !email || !isPasswordValid || !passwordsMatch) { setError('Please fill in all required fields with valid data'); return; } setError(null); setStep('terms'); }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!agreeTerms || !agreePrivacy || !agreeDataProcessing || (selectedOrgType === 'vvb' && !agreeVVBTerms)) {
      setError('Please agree to all terms and conditions')
      return
    }
    setIsSubmitting(true)
    try {
      const supabase = createClient()
      const { error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          // Without this, the confirmation link falls back to the Site URL and
          // drops the newly confirmed user on the public front page with no
          // session (found in the first real registration test, 01.09.2026).
          // The callback exchanges the code and lands them in the portal.
          emailRedirectTo: `${window.location.origin}/auth/callback?next=/portal/dashboard`,
          data: {
            first_name: firstName,
            last_name: lastName,
            org_name: orgName,
            org_type: selectedOrgType,
            country,
          },
        },
      })
      if (signUpError) {
        setError(signUpError.message)
      } else {
        setStep('confirmation')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  const inputStyle: React.CSSProperties = { width: '100%', padding: '12px 16px', borderRadius: '8px', border: '2px solid var(--mcc-border)', fontSize: '15px', outline: 'none', transition: 'border-color 0.2s', backgroundColor: 'white', boxSizing: 'border-box' as const }
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: '14px', fontWeight: 600, color: 'var(--mcc-ink)', marginBottom: '8px' }
  const btnPrimary: React.CSSProperties = { flex: 1, padding: '14px', borderRadius: '8px', border: 'none', backgroundColor: 'var(--mcc-navy)', color: 'white', fontSize: '16px', fontWeight: 700, cursor: 'pointer', transition: 'background-color 0.2s', boxShadow: '0 4px 12px rgba(10,46,77,0.25)' }
  const btnOutline: React.CSSProperties = { flex: 1, padding: '14px', borderRadius: '8px', border: '2px solid var(--mcc-border)', backgroundColor: 'white', color: 'var(--mcc-ink)', fontSize: '16px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, var(--mcc-deep) 0%, var(--mcc-navy) 40%, var(--mcc-navy) 70%, var(--mcc-surface) 100%)', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", padding: '24px' }}>
      <div style={{ width: '100%', maxWidth: '640px' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Link href="/" style={{ textDecoration: 'none' }}>
            <div style={{ width: '64px', height: '64px', backgroundColor: 'var(--mcc-navy)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: '0 8px 32px rgba(10,46,77,0.3)' }}>
              <span style={{ color: 'white', fontWeight: 800, fontSize: '24px', letterSpacing: '-0.5px' }}>MCC</span>
            </div>
          </Link>
          <h1 style={{ fontSize: '24px', fontWeight: 600, color: 'white', marginBottom: '4px', letterSpacing: '-0.3px' }}>Marine Conservation Credits</h1>
          <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)' }}>Create Your Account</p>
        </div>

        {/* Progress Bar */}
        {step !== 'confirmation' && (
          <div style={{ marginBottom: '24px' }}>
            <div style={{ height: '6px', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ height: '100%', backgroundColor: 'var(--mcc-sea)', borderRadius: '3px', transition: 'width 0.4s ease', width: `${progressPercentage[step]}%` }} />
            </div>
            <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginTop: '8px' }}>{stepLabels[step]}</p>
          </div>
        )}

        {/* Card */}
        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 12px 40px rgba(11,25,41,0.16)', overflow: 'hidden' }}>
          <div style={{ padding: '36px' }}>

            {/* Error Display */}
            {error && (
              <div style={{ padding: '14px 16px', borderRadius: '8px', backgroundColor: 'var(--mcc-danger-bg)', border: '1px solid #fecaca', marginBottom: '20px' }}>
                <p style={{ fontSize: '14px', fontWeight: 500, color: 'var(--mcc-danger-fg)', margin: 0 }}>{error}</p>
              </div>
            )}

            {/* STEP 1: ORG TYPE */}
            {step === 'orgType' && (
              <>
                <h2 style={{ fontSize: '26px', fontWeight: 600, color: 'var(--mcc-navy)', marginBottom: '6px' }}>Select Organization Type</h2>
                <p style={{ fontSize: '15px', color: 'var(--mcc-muted)', marginBottom: '28px' }}>Choose the type that best describes your organization</p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '28px' }}>
                  {ORG_TYPES.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => type.enabled && setSelectedOrgType(type.id)}
                      disabled={!type.enabled}
                      style={{
                        padding: '16px',
                        borderRadius: '8px',
                        border: selectedOrgType === type.id ? '2px solid var(--mcc-navy)' : '2px solid var(--mcc-border)',
                        backgroundColor: selectedOrgType === type.id ? 'var(--mcc-sky)' : !type.enabled ? 'var(--mcc-surface)' : 'white',
                        cursor: type.enabled ? 'pointer' : 'not-allowed',
                        opacity: type.enabled ? 1 : 0.5,
                        textAlign: 'left' as const,
                        transition: 'all 0.2s',
                      }}
                    >
                      <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--mcc-ink)', marginBottom: '4px' }}>{type.title}</div>
                      <div style={{ fontSize: '13px', color: 'var(--mcc-muted)', lineHeight: 1.4 }}>{type.description}</div>
                    </button>
                  ))}
                </div>

                <button onClick={handleNextFromOrgType} style={{ ...btnPrimary, width: '100%' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--mcc-navy)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--mcc-navy)'; }}>
                  Continue
                </button>
              </>
            )}

            {/* STEP 2: ORG DETAILS */}
            {step === 'orgDetails' && (
              <>
                <h2 style={{ fontSize: '26px', fontWeight: 600, color: 'var(--mcc-navy)', marginBottom: '6px' }}>Organization Details</h2>
                <p style={{ fontSize: '15px', color: 'var(--mcc-muted)', marginBottom: '28px' }}>Provide information about your organization</p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div>
                    <label style={labelStyle}>Organization Name *</label>
                    <input type="text" placeholder="Your Organization" value={orgName} onChange={(e) => setOrgName(e.target.value)} required style={inputStyle}
                      onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-navy)'; }} onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-border)'; }} />
                  </div>
                  <div>
                    <label style={labelStyle}>Registration Number *</label>
                    <input type="text" placeholder="e.g., Company Registration Number" value={regNumber} onChange={(e) => setRegNumber(e.target.value)} required style={inputStyle}
                      onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-navy)'; }} onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-border)'; }} />
                  </div>
                  <div>
                    <label style={labelStyle}>Country *</label>
                    <select value={country} onChange={(e) => setCountry(e.target.value)} required style={{ ...inputStyle, cursor: 'pointer' }}
                      onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-navy)'; }} onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-border)'; }}>
                      <option value="">Select a country</option>
                      {COUNTRIES.map((c) => (<option key={c} value={c}>{c}</option>))}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Website (optional)</label>
                    <input type="url" placeholder="https://example.com" value={website} onChange={(e) => setWebsite(e.target.value)} style={inputStyle}
                      onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-navy)'; }} onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-border)'; }} />
                  </div>
                  <div>
                    <label style={labelStyle}>Organization Email *</label>
                    <input type="email" placeholder="contact@organization.com" value={orgEmail} onChange={(e) => setOrgEmail(e.target.value)} required style={inputStyle}
                      onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-navy)'; }} onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-border)'; }} />
                  </div>
                  <div>
                    <label style={labelStyle}>Phone (optional)</label>
                    <input type="tel" placeholder="+44 20 7946 0958" value={phone} onChange={(e) => setPhone(e.target.value)} style={inputStyle}
                      onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-navy)'; }} onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-border)'; }} />
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button onClick={() => setStep('orgType')} style={btnOutline}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--mcc-surface)'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}>
                      Back
                    </button>
                    <button onClick={handleNextFromOrgDetails} style={btnPrimary}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--mcc-navy)'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--mcc-navy)'; }}>
                      Continue
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* STEP 3: ACCOUNT DETAILS */}
            {step === 'accountDetails' && (
              <>
                <h2 style={{ fontSize: '26px', fontWeight: 600, color: 'var(--mcc-navy)', marginBottom: '6px' }}>Account Details</h2>
                <p style={{ fontSize: '15px', color: 'var(--mcc-muted)', marginBottom: '28px' }}>Create your personal account credentials</p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={labelStyle}>First Name *</label>
                      <input type="text" placeholder="John" value={firstName} onChange={(e) => setFirstName(e.target.value)} required style={inputStyle}
                        onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-navy)'; }} onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-border)'; }} />
                    </div>
                    <div>
                      <label style={labelStyle}>Last Name *</label>
                      <input type="text" placeholder="Doe" value={lastName} onChange={(e) => setLastName(e.target.value)} required style={inputStyle}
                        onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-navy)'; }} onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-border)'; }} />
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>Email Address *</label>
                    <input type="email" placeholder="john@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required style={inputStyle}
                      onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-navy)'; }} onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-border)'; }} />
                  </div>

                  {/* Password with strength */}
                  <div>
                    <label style={labelStyle}>Password *</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Create a strong password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        style={{ ...inputStyle, paddingRight: '48px' }}
                        onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-navy)'; }}
                        onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-border)'; }}
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--mcc-muted)', padding: '4px' }}>
                        {showPassword ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        )}
                      </button>
                    </div>

                    {password && (
                      <div style={{ marginTop: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <span style={{ fontSize: '12px', color: 'var(--mcc-muted)' }}>Strength:</span>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: passwordStrength.color }}>{passwordStrength.label}</span>
                        </div>
                        <div style={{ height: '4px', backgroundColor: 'var(--mcc-border)', borderRadius: '2px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', borderRadius: '2px', transition: 'all 0.3s', backgroundColor: passwordStrength.color, width: `${((passwordStrength.score + 1) / 4) * 100}%` }} />
                        </div>
                      </div>
                    )}

                    <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {PASSWORD_REQUIREMENTS.map((req) => (
                        <div key={req.label} style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px', color: req.regex.test(password) ? 'var(--mcc-sea)' : 'var(--mcc-muted)' }}>
                          <span>{req.regex.test(password) ? '✓' : '○'}</span>
                          {req.label}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>Confirm Password *</label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      style={{ ...inputStyle, borderColor: confirmPassword && !passwordsMatch ? 'var(--mcc-danger-fg)' : 'var(--mcc-border)' }}
                      onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-navy)'; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = confirmPassword && !passwordsMatch ? 'var(--mcc-danger-fg)' : 'var(--mcc-border)'; }}
                    />
                    {confirmPassword && !passwordsMatch && (
                      <p style={{ fontSize: '13px', color: 'var(--mcc-danger-fg)', marginTop: '6px' }}>Passwords do not match</p>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button onClick={() => setStep('orgDetails')} style={btnOutline}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--mcc-surface)'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}>
                      Back
                    </button>
                    <button onClick={handleNextFromAccountDetails} style={btnPrimary}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--mcc-navy)'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--mcc-navy)'; }}>
                      Continue
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* STEP 4: TERMS */}
            {step === 'terms' && (
              <>
                <h2 style={{ fontSize: '26px', fontWeight: 600, color: 'var(--mcc-navy)', marginBottom: '6px' }}>Terms & Agreements</h2>
                <p style={{ fontSize: '15px', color: 'var(--mcc-muted)', marginBottom: '28px' }}>Please review and accept our terms</p>

                <form onSubmit={handleSubmit}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '28px' }}>
                    {[
                      { checked: agreeTerms, onChange: setAgreeTerms, label: 'Programme Terms', required: true },
                      { checked: agreePrivacy, onChange: setAgreePrivacy, label: 'Privacy Policy', required: true },
                      { checked: agreeDataProcessing, onChange: setAgreeDataProcessing, label: 'Data Processing Agreement', required: true, noLink: true },
                    ].map((item, idx) => (
                      <label key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={item.checked} onChange={(e) => item.onChange(e.target.checked)} required={item.required}
                          style={{ width: '18px', height: '18px', accentColor: 'var(--mcc-navy)', cursor: 'pointer', marginTop: '2px', flexShrink: 0 }} />
                        <span style={{ fontSize: '14px', color: 'var(--mcc-ink)' }}>
                          I agree to the{' '}
                          {item.noLink ? item.label : (
                            <Link href="/terms" style={{ color: 'var(--mcc-navy)', fontWeight: 600, textDecoration: 'none' }}>{item.label}</Link>
                          )}
                          *
                        </span>
                      </label>
                    ))}

                    {selectedOrgType === 'vvb' && (
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={agreeVVBTerms} onChange={(e) => setAgreeVVBTerms(e.target.checked)} required
                          style={{ width: '18px', height: '18px', accentColor: 'var(--mcc-navy)', cursor: 'pointer', marginTop: '2px', flexShrink: 0 }} />
                        <span style={{ fontSize: '14px', color: 'var(--mcc-ink)' }}>
                          I agree to the <Link href="/terms" style={{ color: 'var(--mcc-navy)', fontWeight: 600, textDecoration: 'none' }}>VVB Terms</Link>*
                        </span>
                      </label>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button type="button" onClick={() => setStep('accountDetails')} style={btnOutline}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--mcc-surface)'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}>
                      Back
                    </button>
                    <button type="submit" disabled={isSubmitting}
                      style={{ ...btnPrimary, backgroundColor: isSubmitting ? 'var(--mcc-faint)' : 'var(--mcc-navy)', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                      onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = 'var(--mcc-navy)'; }}
                      onMouseLeave={(e) => { if (!isSubmitting) e.currentTarget.style.backgroundColor = 'var(--mcc-navy)'; }}>
                      {isSubmitting ? 'Submitting...' : 'Create Account'}
                    </button>
                  </div>
                </form>
              </>
            )}

            {/* STEP 5: CONFIRMATION */}
            {step === 'confirmation' && (
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div style={{ width: '64px', height: '64px', backgroundColor: 'var(--mcc-success-bg)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--mcc-sea)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                </div>

                <h2 style={{ fontSize: '26px', fontWeight: 600, color: 'var(--mcc-navy)', marginBottom: '10px' }}>Registration Submitted</h2>
                <p style={{ fontSize: '15px', color: 'var(--mcc-ink)', marginBottom: '8px' }}>
                  Thank you for registering with Marine Conservation Credits!
                </p>
                <p style={{ fontSize: '14px', color: 'var(--mcc-muted)', marginBottom: '24px' }}>
                  Your account is pending verification. You&apos;ll receive an email once your organization has been approved by our team.
                </p>

                <div style={{ backgroundColor: 'var(--mcc-sky)', border: '1px solid var(--mcc-sky)', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
                  <p style={{ fontSize: '14px', color: 'var(--mcc-teal)', margin: 0 }}>
                    Check your email at <strong>{email}</strong> for further instructions.
                  </p>
                </div>

                <Link href="/auth/login" style={{ textDecoration: 'none', display: 'block' }}>
                  <button type="button" style={{ ...btnPrimary, width: '100%' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--mcc-navy)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--mcc-navy)'; }}>
                    Return to Login
                  </button>
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Already have account */}
        {step !== 'confirmation' && (
          <p style={{ textAlign: 'center', fontSize: '14px', color: 'rgba(255,255,255,0.6)', marginTop: '24px' }}>
            Already have an account?{' '}
            <Link href="/auth/login" style={{ textDecoration: 'none', color: 'var(--mcc-sea)', fontWeight: 600 }}>Sign in</Link>
          </p>
        )}
      </div>
    </div>
  )
}
